import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Service, ServiceLinkedService } from '@turizi/shared';
import { Button, Loader, Badge, ActionIcon, Tooltip } from '@mantine/core';
import { useServiceLinkedServices, useCreateServiceLinkedService, useDeleteServiceLinkedService } from '@/hooks/api-hooks';
import { IconPlus, IconTrash, IconLinkOff } from '@tabler/icons-react';
import ServicesListWithFilters from './services-list-with-filters';
import DrawerPage from './drawer-page';
import { ServiceTitle } from './services/service-title';
import { TagIcon } from '@/lib/tag-utils';
import { notify } from '@/lib/notify';

interface Props {
    service: Service;
}

export default function LinkedServiceSelector({ service }: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);

    const {
        data: linkedServicesData,
        isLoading: loadingLinkedServices
    } = useServiceLinkedServices({ service_id: service.id });

    const createLinkMutation = useCreateServiceLinkedService();
    const deleteLinkMutation = useDeleteServiceLinkedService();

    const links: ServiceLinkedService[] = linkedServicesData?.data || [];

    const handleSelect = async (selectedService: Service) => {
        if (selectedService.id === service.id) {
            notify.error(t('cannot_link_service_to_itself'), '');
            return;
        }

        const alreadyLinked = links.some(l => Number(l.linked_service_id) === Number(selectedService.id));
        if (alreadyLinked) {
            notify.error(t('service_already_linked'), '');
            setOpened(false);
            return;
        }

        try {
            await createLinkMutation.mutateAsync({
                service_id: service.id,
                linked_service_id: selectedService.id
            });
            notify.success(t('service_linked_successfully'), '');
            setOpened(false);
        } catch {
            // Error handled by mutation
        }
    };

    const handleRemove = async (linkId: number) => {
        try {
            await deleteLinkMutation.mutateAsync(linkId);
            notify.success(t('service_link_removed'), '');
        } catch {
            // Error handled by mutation
        }
    };

    return (
        <div className='solid-box-border space-y-3'>
            {/* Header */}
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-md font-bold text-gray-900">
                        {t('linked_services')}
                    </span>
                    <Badge size="sm" variant="light" color="blue">
                        {links.length}
                    </Badge>
                </div>
                <Button
                    size="xs"
                    color='black'
                    radius="xl"
                    onClick={() => setOpened(true)}
                    leftSection={<IconPlus size={14} />}
                    loading={createLinkMutation.isPending}
                >
                    {t('add_linked_service')}
                </Button>
            </div>

            {/* List or States */}
            {loadingLinkedServices ? (
                <div className="flex justify-center p-6">
                    <Loader size="sm" />
                </div>
            ) : links.length > 0 ? (
                <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white overflow-hidden shadow-2xs">
                    {links.map(link => {
                        const linkedService = link.linked_service;
                        if (!linkedService) return null;

                        const providerOrSeller = linkedService.seller?.name || linkedService.providers?.[0]?.name;

                        return (
                            <div
                                key={link.id}
                                className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50/70 transition-colors"
                            >
                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                        <TagIcon name={linkedService.category} size="xs" hideText />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <Badge size="xs" variant="outline" color="gray" className="shrink-0 font-mono">
                                                #{linkedService.id}
                                            </Badge>
                                            <span className="text-xs font-semibold text-gray-900 truncate">
                                                <ServiceTitle service={linkedService} links={false} />
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-xxs text-gray-400 mt-0.5 truncate">
                                            <span className="capitalize">{t(linkedService.category)}</span>
                                            {providerOrSeller && (
                                                <>
                                                    <span>•</span>
                                                    <span className="text-gray-600 font-medium truncate">{providerOrSeller}</span>
                                                </>
                                            )}
                                            {linkedService.city && (
                                                <>
                                                    <span>•</span>
                                                    <span>{linkedService.city}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <Tooltip label={t('delete')} position="left" withArrow>
                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        size="sm"
                                        radius="md"
                                        onClick={() => handleRemove(link.id)}
                                        loading={deleteLinkMutation.isPending && deleteLinkMutation.variables === link.id}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-6 px-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50 text-center">
                    <IconLinkOff size={28} className="text-gray-300 mb-1.5" stroke={1.5} />
                    <div className="text-xs font-medium text-gray-500">
                        {t('no_linked_services')}
                    </div>
                </div>
            )}

            <DrawerPage
                opened={opened}
                onClose={() => {
                    setOpened(false);
                }}
                title={t('add_linked_service')}
            >
                <ServicesListWithFilters
                    onSelect={handleSelect}
                />
            </DrawerPage>
        </div>
    );
}